/**
 * THE CONSOLE'S ONE LOADER (Hamzah, 2026-10-20, the loading rule; `loader.css` has it in full). The
 * scene in `scene.ts` on a canvas, first the bank Overview's, with the line under it saying what is
 * being read ("Reading Alarms…"). Two sizes of the same ring:
 *
 *   full    a screen's first open, centred, held by `ScreenGate` while `useFirstLoad` waits for the
 *           screen's first fetches, and kept up for `RESOLVE_MS` once they are in (`useLoaderPhase`),
 *           so the data arriving is seen to land. The Overview's adds the plant step it is reading
 *           (`steps`) and the site's own load during the resolve (`kw`).
 *   small   a panel with no known shape (a drawer, a pop-up, an admin list), for as long as it reads.
 *
 * Every screen and panel uses this one, never its own; there is no spinner and no "Loading…".
 *
 * The frame loop is this component's and stops when it unmounts, while the tab is hidden, and under
 * reduced motion, where one still frame of the powered plant is drawn instead. The scene's ink comes
 * from the console's own tokens, read from the page whenever the theme changes, so a person's accent
 * and light or dark carry into it.
 */
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useTheme, type Theme } from '@/app/theme';
import { CH, CW, drawScene, FACES, hexOf, STILL_T, stepFor, type SceneLabel, type ScenePalette } from './scene';
import './loader.css';

/* The tokens' values when the page cannot be read (a test, a server render): the light theme's. */
const FALLBACK = { fg: '#0b1216', muted: '#4b5b65', border: '#c3ced6', brand: '#15803d' };

/** The console's tokens as `#rrggbb`, through a canvas so any CSS colour form normalises. */
function readPalette(theme: Theme): ScenePalette {
  const faces = FACES[theme];
  if (typeof document === 'undefined') return { ...FALLBACK, ...faces };
  const css = getComputedStyle(document.documentElement);
  const probe = document.createElement('canvas').getContext('2d');
  const tok = (name: string, fallback: string): string => {
    const raw = css.getPropertyValue(name).trim();
    if (!raw || !probe) return hexOf(raw) ?? fallback;
    probe.fillStyle = fallback;
    probe.fillStyle = raw;
    return hexOf(String(probe.fillStyle)) ?? fallback;
  };
  return {
    fg: tok('--fg', FALLBACK.fg),
    muted: tok('--muted-fg', FALLBACK.muted),
    border: tok('--border', FALLBACK.border),
    brand: tok('--brand', FALLBACK.brand),
    ...faces,
  };
}

export function RingLoader({
  label,
  size = 'full',
  done = false,
  kw = null,
  steps = false,
  theme: themeIn,
}: {
  /** What is being read, in the line under the ring ("Reading Alarms…") and on the scene's screen. */
  label: string;
  /** `full` for a screen's first open, `small` for a drawer, a pop-up or an admin list. */
  size?: 'full' | 'small';
  /** The data is in: no reading leaves after this, the screen flashes, and the loader resolves. */
  done?: boolean;
  /** The site's own load, shown on the screen only once `done` — never before, never invented. */
  kw?: number | null;
  /** Name the piece of the plant being read beside the line (the Overview's). */
  steps?: boolean;
  /** The theme, when the caller holds it; else the console's own. */
  theme?: Theme | undefined;
}): ReactNode {
  const { theme: current } = useTheme();
  const theme = themeIn ?? current;
  const small = size === 'small';
  /* The scene's screen carries the name only where it is large enough to read. */
  const site = small ? '' : label;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stepRef = useRef<HTMLSpanElement | null>(null);
  const startRef = useRef(0);
  const doneAtRef = useRef(-1);
  const labelRef = useRef<SceneLabel>({ site, kw });
  /* The theme store applies its tokens to the page before it re-renders, so they are read here. */
  const palette = useMemo(() => readPalette(theme), [theme]);

  useEffect(() => {
    labelRef.current = { site, kw };
  }, [site, kw]);

  useEffect(() => {
    if (done && doneAtRef.current < 0) doneAtRef.current = performance.now();
  }, [done]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;
    if (startRef.current === 0) startRef.current = performance.now();
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = CW * dpr;
    canvas.height = CH * dpr;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    let raf = 0;
    let lastStep = '';

    const paint = (t: number, dn: number): void => {
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, CW, CH);
      ctx.setLineDash([]);
      ctx.textAlign = 'left';
      drawScene(ctx, t, palette, dn, labelRef.current);
      const step = !steps ? '' : dn >= 0 ? 'ready' : reduce.matches ? 'grid, UPS and racks' : stepFor(t);
      if (step !== lastStep && stepRef.current) {
        lastStep = step;
        stepRef.current.textContent = step;
      }
    };
    const frame = (now: number): void => {
      const dn = doneAtRef.current < 0 ? -1 : (now - doneAtRef.current) / 1000;
      paint((now - startRef.current) / 1000, dn);
      raf = requestAnimationFrame(frame);
    };
    const start = (): void => {
      cancelAnimationFrame(raf);
      if (reduce.matches) {
        paint(STILL_T, doneAtRef.current < 0 ? -1 : 2);
        return;
      }
      if (!document.hidden) raf = requestAnimationFrame(frame);
    };
    const onVisibility = (): void => {
      if (document.hidden) cancelAnimationFrame(raf);
      else start();
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);
    reduce.addEventListener('change', start);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
      reduce.removeEventListener('change', start);
    };
    /* `done` restarts the loop only so the reduced-motion still frame is redrawn as ready. */
  }, [palette, done, steps]);

  return (
    <div role="status" className={`lm-ldr ${small ? 'is-small' : ''} ${done ? 'is-done' : ''}`}>
      <span className="sr-only">Reading {label}</span>
      <canvas ref={canvasRef} aria-hidden="true" className="lm-ldr-canvas" />
      <div className="lm-ldr-status" aria-hidden="true">
        <span className="lm-ldr-dot" />
        <b>{steps ? `Reading ${label}` : `Reading ${label}…`}</b>
        {steps && <span ref={stepRef} className="lm-ldr-step" />}
      </div>
    </div>
  );
}
